// 📄 useHes.ts
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros.
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveHesConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, HesFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const HES_STEP_IDS = ['hes_params', 'hes_badis'];

const STEP_KEY_MAP: Record<string, string> = {
  hes_params: 'step_params', hes_badis: 'step_badis'
};

const DEFAULT_FORM: HesFormData = {
  entrySheetCreateTimeout: '20',
  badiSesCreateActive: false, badiSesDeleteActive: false,
  requiresOT: false, otNumber: '', comments: '',
};

export function useHes() {
  const { projectId } = useProject();
  const [activeStep, setActiveStep] = useState('hes_params');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<HesFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: keyof HesFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    if (devData.checklistProgress) {
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && HES_STEP_IDS.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<HesFormData> = {};
    const hes = devData.channels?.serviceEntrySheet;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.serviceEntrySheet : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (hes) {
      if (hes.step_params) {
        if (activeStep === 'hes_params') {
          const requiresOT = hes.step_params.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? hes.step_params : (envChannelData?.step_params || {});
          initialFormData.entrySheetCreateTimeout = source.entrySheetCreateTimeout || '20';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, hes_params: hes.step_params.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, hes_params: hes.step_params.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, hes_params: hes.step_params.customDevelopment || emptyCustomDevelopment() }));
        qasMap.hes_params = hes.step_params.transport?.QAS || emptyTransportConfirmation();
        prdMap.hes_params = hes.step_params.transport?.PRD || emptyTransportConfirmation();
      }
      if (hes.step_badis) {
        if (activeStep === 'hes_badis') {
          const requiresOT = hes.step_badis.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? hes.step_badis : (envChannelData?.step_badis || {});
          initialFormData.badiSesCreateActive = source.badiSesCreateActive || false;
          initialFormData.badiSesDeleteActive = source.badiSesDeleteActive || false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, hes_badis: hes.step_badis.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, hes_badis: hes.step_badis.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, hes_badis: hes.step_badis.customDevelopment || emptyCustomDevelopment() }));
        qasMap.hes_badis = hes.step_badis.transport?.QAS || emptyTransportConfirmation();
        prdMap.hes_badis = hes.step_badis.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.serviceEntrySheet;
    const prdChannelData = configSource?.environments?.PRD?.channels?.serviceEntrySheet;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    HES_STEP_IDS.forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración de HES:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    if (activeStep === 'hes_params' && !formData.entrySheetCreateTimeout.trim()) {
      setValidationError("El valor de TIMEOUT es obligatorio."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveHesConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'serviceEntrySheet', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'serviceEntrySheet', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep, setActiveStep, completedSteps, formData, handleFieldChange,
    requiresOTMap, setRequiresOTMap, otNumberMap, setOtNumberMap,
    customDevMap, setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError, setValidationError, saveStatus, loadingData,
    handleSave, selectedEnv, handleEnvChange,
  };
}
